/**
 * Export/Import Draft utilities for slow-network users.
 * Used on Listing pages: Book, Blog, Tirth.
 *
 * Export: downloads the current form draft (or a selected record) as JSON.
 * Import: reads a JSON file and stores it in localStorage as a draft,
 *         then navigates to the add/edit form so the draft-restore dialog picks it up.
 */

/**
 * Download an object as a JSON file.
 * @param {object} data - The data to export.
 * @param {string} filename - Suggested filename (without extension).
 */
export const exportToJson = (data, filename = "export") => {
  try {
    const json = JSON.stringify({ data, timestamp: Date.now() }, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  } catch (err) {
    console.error("Export failed:", err);
    return false;
  }
};

/**
 * Read a JSON file selected by the user.
 * Returns a Promise that resolves with { data, timestamp } or rejects on error.
 * @param {File} file - The File object from the file input.
 */
export const readJsonFile = (file) => {
  return new Promise((resolve, reject) => {
    if (!file || file.type !== "application/json") {
      reject(new Error("Please select a valid JSON file."));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        resolve(parsed);
      } catch {
        reject(new Error("Invalid JSON file. Please check the file and try again."));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsText(file);
  });
};

/**
 * Import JSON data into localStorage as a draft, then navigate to the form.
 * The form's existing draft-restore dialog will automatically prompt the user.
 *
 * @param {File} file - The JSON file to import.
 * @param {string} draftKey - localStorage key e.g. "draft_book", "draft_blog", "draft_tirth".
 * @param {function} navigate - React Router navigate function.
 * @param {string} addPath - Route path to navigate to e.g. "/book/add".
 * @param {function} onSuccess - Callback called with a success message.
 * @param {function} onError - Callback called with an error message.
 */
export const importJsonDraft = async (
  file,
  draftKey,
  navigate,
  addPath,
  onSuccess,
  onError
) => {
  try {
    const parsed = await readJsonFile(file);
    // Support both formats: { data, timestamp } and plain objects
    const draftData = parsed?.data ?? parsed;
    if (!draftData || typeof draftData !== "object") {
      onError?.("Invalid format: JSON must contain a data object.");
      return;
    }
    // Store into localStorage — the form's useEffect will pick it up
    localStorage.setItem(
      draftKey,
      JSON.stringify({ data: draftData, timestamp: Date.now() })
    );
    onSuccess?.("Draft imported! Redirecting to form...");
    setTimeout(() => navigate(addPath), 800);
  } catch (err) {
    onError?.(err.message || "Failed to import JSON.");
  }
};
